import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Laptop } from 'lucide-react';
import {
  allocateHardwareDevice, getHardwareAllocations, removeHardwareAllocation,
  type HwAllocation, type HwAllocDevice,
} from '@/api/hardwareAllocation';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { AlertDialog } from '@/components/ui/AlertDialog';
import {
  CoverageHint, CoveragePanel, ResizablePalette, SelectRow, type CoverageItem,
} from '@/components/common/allocationPanel';

const KEY = ['hardware-allocations'] as const;

type View = 'device' | 'department';

/**
 * Onboarding — Hardware Allocation (Phase 11, design §12).
 *
 * The hardware twin of Software Allocation, and deliberately the same shape:
 * two symmetric lenses over the same rows, pick an item on the left, and the
 * right panel splits the other side into Allocated / Not allocated.
 *
 * ⛔ A DEVICE IS RESTRICTED IF, AND ONLY IF, IT HAS AT LEAST ONE ALLOCATION.
 * There is no flag to turn on — migration 061 had one and it was retired
 * (§13.6), because "flagged with nothing allocated = offered to nobody" was a
 * hazard that existed only because the flag could be on with no rows.
 *
 * ⛔ WHICH MEANS THE FIRST ALLOCATION IS THE SWITCH, and there is no staging
 * state. Allocating one department to the laptop removes it from every other
 * department THAT INSTANT. This screen therefore confirms before the first
 * allocation on a device — the only place that warning can live, because the
 * database cannot tell "the first one" from "the fifth" in a way that helps a
 * person.
 */
export function HardwareAllocation() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  const [view, setView] = useState<View>('device');
  const [selectedDevice, setSelectedDevice] = useState<number | null>(null);
  const [selectedDept, setSelectedDept] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmFirst, setConfirmFirst] = useState<{ device: HwAllocDevice; code: string } | null>(null);
  const [removeTarget, setRemoveTarget] = useState<HwAllocation | null>(null);
  const [leftQuery, setLeftQuery] = useState('');

  const query = useQuery({ queryKey: KEY, queryFn: ({ signal }) => getHardwareAllocations(signal) });

  const allocations = useMemo(() => query.data?.Allocations ?? [], [query.data]);
  const allDevices = useMemo(() => query.data?.Devices ?? [], [query.data]);
  const departments = useMemo(() => query.data?.Departments ?? [], [query.data]);

  const byDevice = useMemo(() => {
    const map = new Map<number, HwAllocation[]>();
    for (const a of allocations) map.set(a.DeviceOptionId, [...(map.get(a.DeviceOptionId) ?? []), a]);
    return map;
  }, [allocations]);

  /**
   * ⛔ NOT EVERY DEVICE CAN CARRY A DEPARTMENT.
   *
   * ⚠ Anthony, 06/10/2026: *"that is the same for hardware allocation — they
   * should be hidden, that includes retired items."*
   *
   * - **Branch kit** goes to a location, and a department is an attribute of a
   *   person. Since the same day, branch kit is not offered on a personal
   *   request at all — so an allocation on one would narrow a list nobody is
   *   ever shown.
   * - **A retired device** is offered to nobody by anybody, so entitling a
   *   department to it configures nothing.
   *
   * ⛔ EXCEPT ONE THAT ALREADY CARRIES AN ALLOCATION, which stays listed. This
   * is the same rule the coverage panel below already applies to a department
   * that no longer exists: hiding the device would hide its rows with it and
   * leave something in the database that nobody could see or remove. The row is
   * badged so it does not read as an ordinary choice.
   *
   * ⚠ The server refuses a NEW allocation on either kind, so a stale board
   * cannot create one — this only decides what is offered.
   */
  const devices = useMemo(
    () => allDevices.filter((d) =>
      (d.IsActive && !d.IsBranchUse) || (byDevice.get(d.DeviceOptionId) ?? []).length > 0),
    [allDevices, byDevice]);

  /**
   * Why a device is listed although nothing may be allocated to it, or
   * undefined for an ordinary one.
   *
   * ⚠ Branch use is reported FIRST. A device can be both retired and branch
   * kit, and "for a location" is the fact that explains why it was never a
   * department's to request in the first place.
   */
  const strandedReason = (d: HwAllocDevice): string | undefined =>
    d.IsBranchUse ? '(for a location — allocations here do nothing)'
      : !d.IsActive ? '(retired — allocations here do nothing)'
        : undefined;

  const byDept = useMemo(() => {
    const map = new Map<string, HwAllocation[]>();
    for (const a of allocations) map.set(a.DepartmentCode, [...(map.get(a.DepartmentCode) ?? []), a]);
    return map;
  }, [allocations]);

  // ⚠ The left list and the coverage panel search INDEPENDENTLY. They are two
  // different questions — "which device am I configuring" and "which department
  // am I looking for in it" — and one box answering both would make picking a
  // device hide the departments.
  const term = leftQuery.trim().toLowerCase();
  const visibleDevices = useMemo(
    () => devices.filter((d) => !term || d.Name.toLowerCase().includes(term)),
    [devices, term]);
  /**
   * The device list, grouped (090).
   *
   * ⛔ WHY GROUPED AT ALL. Anthony, 07/10/2026: *"we need to group devices so
   * that a role cant have both and its easier to allocated."* Fifteen devices
   * in one flat list gave no hint that four of them were the same choice —
   * which is how four laptop variants came to be allocated separately, leaving
   * 53 of 89 roles with no laptop.
   *
   * ⚠ UNGROUPED COMES LAST, under no heading. Those devices follow the pre-090
   * rule and have no siblings; putting them under a heading called
   * "(ungrouped)" would invent a group that does not exist.
   */
  const deviceSections = useMemo(() => {
    const order: (string | null)[] = [];
    const byGroup = new Map<string | null, HwAllocDevice[]>();

    for (const d of visibleDevices) {
      const key = d.DeviceGroup ?? null;
      if (!byGroup.has(key)) { byGroup.set(key, []); order.push(key); }
      byGroup.get(key)!.push(d);
    }

    return order
      // Ungrouped last; everything else keeps the order the server sent.
      .sort((a, b) => (a === null ? 1 : 0) - (b === null ? 1 : 0))
      .map((group) => {
        const devices = byGroup.get(group)!;
        return {
          group,
          devices,
          // ⛔ ALLOCATED MEANS ANY VARIANT HAS ONE, which is what narrows the
          // whole group. Asked of the group rather than the row, because a
          // variant with no allocations inside an allocated group is hidden
          // from everybody — not offered to everybody.
          allocated: devices.some((d) => (byDevice.get(d.DeviceOptionId) ?? []).length > 0),
          hasDefault: devices.some((d) => d.IsGroupDefault && d.IsActive),
        };
      });
  }, [visibleDevices, byDevice]);

  /**
   * department code → the device in a given group it already holds (090).
   *
   * ⛔ WHAT THE PANEL WAS GETTING WRONG. A role holding Laptop (High Spec)
   * appeared in Laptop - Standard's "Not allocated (60)" with an Allocate
   * button that could only fail — the server refuses a second variant in the
   * same group. Reported 07/10/2026: *"app support manager has been allocated a
   * high spec, therefore the role shouldnt be able to be allocated to another
   * type of laptop."*
   *
   * ⚠ Keyed by group AND department, because the question is always "in THIS
   * group, what does this role already have?".
   */
  const heldByGroup = useMemo(() => {
    const byId = new Map(allDevices.map((d) => [d.DeviceOptionId, d]));
    const map = new Map<string, Map<string, string>>();

    for (const a of allocations) {
      const group = byId.get(a.DeviceOptionId)?.DeviceGroup;
      if (!group) continue;
      const key = group.toLowerCase();
      if (!map.has(key)) map.set(key, new Map());
      // ⚠ First wins, and a role holding two is the data problem this exists
      // to surface rather than hide. The board shows both as ALLOCATED under
      // each variant; this only decides what the other variants say.
      if (!map.get(key)!.has(a.DepartmentCode)) {
        map.get(key)!.set(a.DepartmentCode, a.DeviceName);
      }
    }

    return map;
  }, [allocations, allDevices]);

  /**
   * What this department already holds in this device's group, or undefined.
   *
   * ⛔ THE ONE PLACE THE RULE LIVES. It excludes the device being looked at —
   * that row is ALLOCATED, not blocked — and the panel trusts the answer rather
   * than testing `!alloc` again. There were three such tests at first, each
   * covering the others, so a control could remove any one of them and nothing
   * failed.
   */
  const heldInstead = (device: HwAllocDevice, departmentCode: string): string | undefined => {
    if (!device.DeviceGroup) return undefined;
    const held = heldByGroup.get(device.DeviceGroup.toLowerCase())?.get(departmentCode);
    return held && held !== device.Name ? held : undefined;
  };

  const visibleDepartments = useMemo(
    () => departments.filter((d) => !term
      || `${d.DepartmentCode} ${d.Description ?? ''}`.toLowerCase().includes(term)),
    [departments, term]);

  const selectedDeviceRow = devices.find((d) => d.DeviceOptionId === selectedDevice) ?? null;

  // Which departments hold the selected device, and which do not.
  const deviceItems = useMemo<CoverageItem<HwAllocation>[]>(() => {
    if (!selectedDeviceRow) return [];
    const held = new Map((byDevice.get(selectedDeviceRow.DeviceOptionId) ?? [])
      .map((a) => [a.DepartmentCode, a] as const));

    // ⛔ THE UNION, NOT JUST THE DEPARTMENT LIST. An allocation can name a
    // department that no longer exists in Oscar — it still entitles nobody, but
    // it is still there and somebody has to be able to remove it. Building this
    // from `departments` alone made those rows vanish from the screen while
    // staying in the database.
    const codes = [
      ...departments.map((d) => ({ code: d.DepartmentCode, label: d.Description ?? '' })),
      ...[...held.values()]
        .filter((a) => !departments.some((d) => d.DepartmentCode === a.DepartmentCode))
        .map((a) => ({ code: a.DepartmentCode, label: a.DepartmentDescription ?? '' })),
    ];

    return codes.map(({ code, label }) => ({
      key: code,
      code,
      label,
      mono: true,
      // ⚠ Retired in Oscar since, or gone entirely: the row still works but
      // entitles nobody.
      badge: held.get(code)?.DepartmentIsRetired
        ? (
          <span className="inline-flex shrink-0 items-center gap-1 text-xs text-orange">
            <AlertTriangle size={12} aria-hidden /> no such department
          </span>
        )
        : undefined,
      search: `${code} ${label}`.toLowerCase(),
      alloc: held.get(code) ?? null,
      // ⛔ The role already has one from this group, so this variant cannot be
      // allocated to it — the server refuses it, and the panel must not offer.
      heldElsewhere: heldInstead(selectedDeviceRow, code),
      targetId: selectedDeviceRow.DeviceOptionId,
    }));
    // ⚠ heldInstead closes over heldByGroup, which is the dependency that
    // matters; listing the function itself would rebuild this every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeviceRow, byDevice, departments, heldByGroup]);

  // Which devices the selected department may request, and which it may not.
  const deptItems = useMemo<CoverageItem<HwAllocation>[]>(() => {
    if (!selectedDept) return [];
    const held = new Map((byDept.get(selectedDept) ?? [])
      .map((a) => [a.DeviceOptionId, a] as const));

    return devices.map((d) => ({
      key: String(d.DeviceOptionId),
      code: d.Name,
      // ⚠ Why it is still in a list it would otherwise be hidden from. A
      // stranded device always has rows on it — that is the only reason it is
      // here — so this never fires on an ordinary choice.
      label: strandedReason(d) ?? '',
      mono: false,
      // ⛔ A device with NO allocations is offered to everybody, so showing it
      // as "not allocated" to this department would read as the opposite of the
      // truth. Said on the row rather than left to be inferred.
      badge: (byDevice.get(d.DeviceOptionId) ?? []).length === 0
        ? (
          <span className="shrink-0 rounded-full bg-green/15 px-1.5 py-0.5 text-[10px] font-semibold text-green">
            already offered to everybody
          </span>
        )
        : undefined,
      search: d.Name.toLowerCase(),
      alloc: held.get(d.DeviceOptionId) ?? null,
      // ⛔ The mirror of the by-device lens: this department already holds
      // another variant of this device's group.
      heldElsewhere: heldInstead(d, selectedDept),
      targetId: d.DeviceOptionId,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDept, byDept, byDevice, devices, heldByGroup]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: KEY });
  const onError = (e: unknown) =>
    setError(e instanceof ApiError ? e.message : 'Something went wrong. Please retry.');

  const add = useMutation({
    mutationFn: (v: { DeviceOptionId: number; DepartmentCode: string }) => allocateHardwareDevice(v),
    onSuccess: () => { invalidate(); setError(null); setConfirmFirst(null); },
    onError,
  });

  const remove = useMutation({
    mutationFn: (id: number) => removeHardwareAllocation(id),
    onSuccess: () => { invalidate(); setError(null); setRemoveTarget(null); },
    onError,
  });

  /**
   * ⛔ THE CONFIRM THAT MATTERS, and only on the FIRST one. Asking every time
   * would train people to click through it, which is how a warning stops
   * working.
   */
  function allocate(device: HwAllocDevice, code: string) {
    const isFirst = (byDevice.get(device.DeviceOptionId) ?? []).length === 0;
    if (isFirst) setConfirmFirst({ device, code });
    else add.mutate({ DeviceOptionId: device.DeviceOptionId, DepartmentCode: code });
  }

  return (
    <section>
      <header className="mb-5">
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-navy">
          <Laptop className="h-6 w-6 text-navy" aria-hidden /> Hardware Allocation
        </h1>
        <p className="mt-1 text-sm text-darkGrey/80">
          Which departments may request which device on the Hardware and Mobile Device Request
          form. A device with no allocation is offered to <strong>everybody</strong>.
        </p>
      </header>

      {!isAdmin && (
        <p className="mb-4 rounded-lg border border-border bg-bg px-3 py-2 text-sm text-darkGrey">
          You can see the allocations but not change them. Editing needs the{' '}
          <strong>oscar-admin</strong> role.
        </p>
      )}

      {error && (
        <div role="alert" className="mb-4 rounded-lg border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
          {error}
        </div>
      )}

      {query.isError && (
        <div role="alert" className="mb-4 rounded-lg border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
          The allocations could not be loaded. If migration 077 has not been applied yet, that is why.
        </div>
      )}

      <div role="tablist" aria-label="Allocation view" className="mb-3 flex gap-2">
        {(['device', 'department'] as const).map((v) => (
          <button
            key={v}
            role="tab"
            type="button"
            aria-selected={view === v}
            onClick={() => setView(v)}
            className={`rounded-lg border px-3 py-1.5 text-sm font-semibold ${
              view === v ? 'border-navy bg-navy text-white' : 'border-border bg-white text-darkGrey'
            }`}
          >
            {v === 'device' ? 'By device' : 'By department'}
          </button>
        ))}
      </div>

      {/* ⛔ THE SAME SHAPE AS SOFTWARE ALLOCATION, and now literally the same
          component. The right-hand panel is a fixed, resizable column, which is
          what keeps Allocate next to the department it belongs to — on the old
          full-width panel the button sat about 1,300px away from the name. */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="flex-1">
          <label className="mb-3 flex flex-col text-xs font-semibold text-darkGrey">
            {view === 'device' ? 'Search devices' : 'Search departments'}
            <input
              aria-label={view === 'device' ? 'Search devices' : 'Search departments'}
              value={leftQuery}
              onChange={(e) => setLeftQuery(e.target.value)}
              placeholder={view === 'device' ? 'Device name' : 'Code or description'}
              className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal"
            />
          </label>

          <ul className="space-y-2">
            {view === 'device'
              ? deviceSections.map((section) => (
                  <li key={section.group ?? '(ungrouped)'}>
                    {/* ⛔ THE HEADING IS WHERE THE GROUP'S STATE IS SAID. A group
                        with allocations and no default is one some roles cannot
                        request at all — the exact state that left 53 of 89
                        roles without a laptop — and it is invisible on any row
                        taken by itself. */}
                    {section.group && (
                      <div className="mb-1 flex flex-wrap items-baseline gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-navy">
                          {section.group}
                        </span>
                        <span className="text-xs text-darkGrey/60">
                          a role gets one of these
                        </span>
                        {section.allocated && !section.hasDefault && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-orange/15 px-2 py-0.5 text-[10px] font-semibold text-orange">
                            <AlertTriangle size={11} aria-hidden /> no default — roles with no
                            allocation get nothing from this group
                          </span>
                        )}
                      </div>
                    )}

                    <ul className="space-y-2">
                      {section.devices.map((d) => {
                        const count = (byDevice.get(d.DeviceOptionId) ?? []).length;
                        return (
                          <SelectRow
                            key={d.DeviceOptionId}
                            primary={d.Name}
                            secondary={strandedReason(d)
                              ?? (d.IsGroupDefault ? 'the default for this group' : undefined)}
                            /* ⛔ 0 is not "not set up" — it is "offered to everybody",
                               which is a different and much louder fact.

                               ⚠ Except inside an ALLOCATED group, where 0 means
                               nobody is entitled to this variant specifically —
                               the group is already narrowed, so "everyone" would
                               be the opposite of the truth. */
                            count={count === 0 ? (section.allocated ? 0 : 'everyone') : count}
                            countTone={count === 0 && !section.allocated
                              ? 'bg-green/15 text-green' : 'bg-navy/10 text-navy'}
                            selected={selectedDevice === d.DeviceOptionId}
                            onSelect={() => setSelectedDevice(d.DeviceOptionId)}
                          />
                        );
                      })}
                    </ul>
                  </li>
                ))
              : visibleDepartments.map((dept) => (
                  <SelectRow
                    key={dept.DepartmentCode}
                    primary={dept.DepartmentCode}
                    secondary={dept.Description}
                    mono
                    count={(byDept.get(dept.DepartmentCode) ?? []).length}
                    selected={selectedDept === dept.DepartmentCode}
                    onSelect={() => setSelectedDept(dept.DepartmentCode)}
                  />
                ))}

            {view === 'device' && visibleDevices.length === 0 && !query.isLoading && (
              <li className="text-sm text-darkGrey/60">No devices match.</li>
            )}
            {view === 'department' && visibleDepartments.length === 0 && !query.isLoading && (
              <li className="text-sm text-darkGrey/60">
                {departments.length === 0
                  ? 'No departments could be read from Oscar, so nothing can be allocated.'
                  : 'No departments match.'}
              </li>
            )}
          </ul>
        </div>

        <ResizablePalette>
          {view === 'device' && (selectedDeviceRow
            ? (
              <CoveragePanel<HwAllocation>
                title={<>Coverage — <span className="text-navy">{selectedDeviceRow.Name}</span></>}
                searchPlaceholder="Search departments"
                items={deviceItems}
                admin={isAdmin}
                busy={add.isPending || remove.isPending}
                /* ⛔ The state that is invisible otherwise: no rows means
                   everybody, not "nobody has configured this". */
                note={deviceItems.every((it) => !it.alloc) && (
                  <>No allocations, so <strong>everybody</strong> can request it. Allocating the
                    first department restricts it to that department.</>
                )}
                onAllocate={(it) => allocate(selectedDeviceRow, it.code)}
                onRemove={setRemoveTarget}
              />
            )
            : <CoverageHint>Select a device on the left to see which departments may request it — and which may not.</CoverageHint>)}

          {view === 'department' && (selectedDept
            ? (
              <CoveragePanel<HwAllocation>
                title={<>Coverage — <span className="font-mono text-navy">{selectedDept}</span></>}
                searchPlaceholder="Search devices"
                items={deptItems}
                admin={isAdmin}
                busy={add.isPending || remove.isPending}
                onAllocate={(it) => {
                  const device = devices.find((d) => d.DeviceOptionId === it.targetId);
                  if (device) allocate(device, selectedDept);
                }}
                onRemove={setRemoveTarget}
              />
            )
            : <CoverageHint>Select a department on the left to see which devices it may request — and which it may not.</CoverageHint>)}
        </ResizablePalette>
      </div>


      {/* ⛔ ONLY ON THE FIRST ONE. This is the moment the device stops being
          offered to everybody, and nothing else on the screen says so. */}
      {confirmFirst && (
        <AlertDialog
          open
          onOpenChange={(o) => { if (!o) setConfirmFirst(null); }}
          title={`Restrict ${confirmFirst.device.Name}?`}
          confirmLabel="Restrict it"
          onConfirm={() => add.mutate({
            DeviceOptionId: confirmFirst.device.DeviceOptionId,
            DepartmentCode: confirmFirst.code,
          })}
        >
          <p className="text-sm text-darkGrey">
            <strong>{confirmFirst.device.Name}</strong> is currently offered to everybody. This is
            its first allocation, so from the moment you save it will be offered{' '}
            <strong>only</strong> to {confirmFirst.code} — everyone else loses it immediately.
          </p>
          <p className="mt-2 text-sm text-darkGrey/80">
            Add the other departments straight afterwards, or remove this one to put it back.
          </p>
        </AlertDialog>
      )}

      {removeTarget && (
        <AlertDialog
          open
          onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}
          title={`Remove ${removeTarget.DepartmentCode} from ${removeTarget.DeviceName}?`}
          confirmLabel="Remove"
          onConfirm={() => remove.mutate(removeTarget.DeviceDepartmentId)}
        >
          <p className="text-sm text-darkGrey">
            {removeTarget.DepartmentCode} will no longer be able to request{' '}
            {removeTarget.DeviceName}.
          </p>
          {(byDevice.get(removeTarget.DeviceOptionId) ?? []).length === 1 && (
            <p className="mt-2 rounded border border-orange/40 bg-orange/10 px-2 py-1 text-sm text-darkGrey">
              ⚠ This is the last allocation on {removeTarget.DeviceName}, so it goes back to being
              offered to <strong>everybody</strong>.
            </p>
          )}
        </AlertDialog>
      )}
    </section>
  );
}


